import { useState, useContext, useEffect } from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import CustomAppBar from "../../components/AppBar";
import Footer from "../../components/Footer";
import { Typography } from "@mui/material";

import PeopleIcon from "@mui/icons-material/People";
import StarsIcon from "@mui/icons-material/Stars";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PersonIcon from "@mui/icons-material/Person";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import StarIcon from "@mui/icons-material/Star";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import GavelIcon from "@mui/icons-material/Gavel";
import { AppContext } from "../../scripts/context/App";

const drawerWidth = 240;

interface Props {
  window?: () => Window;
}

const NAV_ITEMS_1 = [
  {
    label: "Users",
    icon: <PeopleIcon />,
    to: "/users-management",
    permission_key: "view_system_user",
  },
  {
    label: "Roles",
    icon: <StarsIcon />,
    to: "/roles-management",
    permission_key: "view_system_role",
  },
  {
    label: "Ticket Categories",
    icon: <ConfirmationNumberIcon />,
    to: "/ticket-categories",
    permission_key: "view_support_category",
  },
];

const NAV_ITEMS_2 = [
  {
    label: "Customers",
    icon: <PersonIcon />,
    to: "/customers-management",
    permission_key: "view_customer",
  },
  {
    label: "Claimer",
    icon: <MonetizationOnIcon />,
    to: "/claimer-management",
    permission_key: "view_claimers",
  },
  {
    label: "Sponsor",
    icon: <StarIcon />,
    to: "/sponsor-management",
    permission_key: "view_sponsors",
  },
  {
    label: "Smart Contract",
    icon: <GavelIcon />,
    to: "/smart-contract-management",
    permission_key: "view_project_details",
  },
  {
    label: "Vesting",
    icon: <ShowChartIcon />,
    to: "/vesting-management",
    permission_key: "view_vesting",
  },
];

export default function DashboardLayout(props: Props) {
  const { window } = props;

  const { user } = useContext(AppContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState<string[]>(["1", "2"]);
  const location = useLocation();
  const activeRoute: any = [...NAV_ITEMS_1, ...NAV_ITEMS_2].find((item) => {
    if (location.pathname.includes("/smart-contract-management")) {
      return item.label === "Smart Contract";
    }

    return (
      item.to ===
      `/${
        location.pathname.split("/")[location.pathname.split("/").length - 1]
      }`
    );
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div className="drawer">
      <Toolbar>
        <a
          style={{
            width: "80%",
            alignSelf: "center",
            paddingTop: "7px",
          }}
          href="https://bullit.app"
          target="_blank"
          rel="noreferrer"
        >
          <img
            src={require("../../assets/brandDark.svg").default}
            alt="brand"
            style={{ width: "100%", userSelect: "none" }}
          />
        </a>
      </Toolbar>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            disableRipple
            onClick={() => {
              if (openCategories.includes("1")) {
                setOpenCategories(openCategories.filter((c) => c !== "1"));
              } else {
                setOpenCategories([...openCategories, "1"]);
              }
            }}
          >
            <Typography variant="body2" color="textSecondary">
              <ArrowForwardIosIcon
                sx={{
                  transform: openCategories.includes("1")
                    ? "rotate(90deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s",
                  fontSize: "11px",
                  marginInlineEnd: "0.5rem",
                }}
              />
              System Management
            </Typography>
          </ListItemButton>
        </ListItem>
        {openCategories.includes("1")
          ? NAV_ITEMS_1.filter((item) =>
              user?.permissions.includes(item.permission_key)
            ).map((text, index) => (
              <NavLink
                key={index}
                to={`/dashboard${text.to}`}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                }}
                onClick={() => setMobileOpen(false)}
              >
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemIcon sx={{ minWidth: "30px" }}>
                      {text.icon}
                    </ListItemIcon>
                    <ListItemText primary={text.label} />
                  </ListItemButton>
                </ListItem>
              </NavLink>
            ))
          : NAV_ITEMS_1.find((item) => item.to === activeRoute?.to) && (
              <NavLink
                to={`/dashboard${activeRoute.to}`}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemIcon sx={{ minWidth: "30px" }}>
                      {activeRoute.icon}
                    </ListItemIcon>
                    <ListItemText primary={activeRoute.label} />
                  </ListItemButton>
                </ListItem>
              </NavLink>
            )}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            disableRipple
            onClick={() => {
              if (openCategories.includes("2")) {
                setOpenCategories(openCategories.filter((c) => c !== "2"));
              } else {
                setOpenCategories([...openCategories, "2"]);
              }
            }}
          >
            <Typography variant="body2" color="textSecondary">
              <ArrowForwardIosIcon
                sx={{
                  transform: openCategories.includes("2")
                    ? "rotate(90deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s",
                  fontSize: "11px",
                  marginInlineEnd: "0.5rem",
                }}
              />
              Other
            </Typography>
          </ListItemButton>
        </ListItem>
        {openCategories.includes("2")
          ? NAV_ITEMS_2.filter((item) =>
              user?.permissions.includes(item.permission_key)
            ).map((text, index) => (
              <NavLink
                key={index}
                to={`/dashboard${text.to}`}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                }}
                onClick={() => setMobileOpen(false)}
              >
                <ListItem key={index} disablePadding>
                  <ListItemButton>
                    <ListItemIcon sx={{ minWidth: "30px" }}>
                      {text.icon}
                    </ListItemIcon>
                    <ListItemText primary={text.label} />
                  </ListItemButton>
                </ListItem>
              </NavLink>
            ))
          : NAV_ITEMS_2.find((item) => item.to === activeRoute?.to) && (
              <NavLink
                to={`/dashboard${activeRoute.to}`}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <ListItem disablePadding>
                  <ListItemButton>
                    <ListItemIcon sx={{ minWidth: "30px" }}>
                      {activeRoute.icon}
                    </ListItemIcon>
                    <ListItemText primary={activeRoute.label} />
                  </ListItemButton>
                </ListItem>
              </NavLink>
            )}
      </List>
    </div>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <CustomAppBar handleDrawerToggle={handleDrawerToggle} />
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 8,
          width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
          maxWidth: "2000px",
          margin: "0 auto",
        }}
      >
        <Outlet />
        <Footer />
      </Box>
    </Box>
  );
}
